import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminDashboard';
import { Users, Search, Mail, Phone, ShieldCheck, User, Clock, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleColors = {
  admin: 'bg-rust/10 text-rust border border-rust/20',
  user:  'bg-green-50 text-green-700 border border-green-200',
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/users`, { withCredentials: true })
      .then(r => setUsers(r.data.users || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = users.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.phone?.includes(search)
  );

  return (
    <AdminLayout active="/admin/users">
      <div data-testid="admin-users">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-dark-brown">Users</h1>
            <p className="font-body text-sm text-light-brown mt-1">All registered customers and admins</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-rust/10 text-rust font-body text-sm font-semibold px-4 py-2 rounded-full">
              {users.length} {users.length === 1 ? 'user' : 'users'}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-brown" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email or phone…"
            className="pl-9 bg-white border-border-custom font-body"
            data-testid="user-search"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-light-brown hover:text-dark-brown" />
            </button>
          )}
        </div>

        {/* Stats Row */}
        {!loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Total Users',  value: users.length,                                icon: Users,       color: 'bg-rust/10 text-rust' },
              { label: 'Admins',       value: users.filter(u => u.role === 'admin').length, icon: ShieldCheck, color: 'bg-amber/10 text-amber-700' },
              { label: 'Verified',     value: users.filter(u => u.email_verified).length,  icon: Mail,        color: 'bg-green-50 text-green-700' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl border border-border-custom p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${s.color} flex items-center justify-center shrink-0`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-heading text-xl font-bold text-dark-brown">{s.value}</div>
                  <div className="font-body text-xs text-light-brown">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-20"><div className="spinner"></div></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-border-custom">
            <Users className="w-12 h-12 text-border-custom mx-auto mb-3" />
            <p className="font-body text-light-brown">{search ? 'No users match your search' : 'No users yet'}</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-border-custom overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-custom bg-cream/50">
                    <th className="text-left font-body text-xs text-light-brown uppercase tracking-wide px-5 py-3">Name</th>
                    <th className="text-left font-body text-xs text-light-brown uppercase tracking-wide px-5 py-3">Email</th>
                    <th className="text-left font-body text-xs text-light-brown uppercase tracking-wide px-5 py-3">Phone</th>
                    <th className="text-left font-body text-xs text-light-brown uppercase tracking-wide px-5 py-3">Role</th>
                    <th className="text-left font-body text-xs text-light-brown uppercase tracking-wide px-5 py-3">Verified</th>
                    <th className="text-left font-body text-xs text-light-brown uppercase tracking-wide px-5 py-3">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u, idx) => (
                    <tr key={u.id || idx} className="border-b border-border-custom last:border-0 hover:bg-cream/30 transition-colors" data-testid={`user-row-${idx}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-rust/10 flex items-center justify-center shrink-0">
                            {u.picture
                              ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                              : <User className="w-4 h-4 text-rust" />
                            }
                          </div>
                          <span className="font-body text-sm font-medium text-dark-brown">{u.name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <a href={`mailto:${u.email}`} className="font-body text-sm text-rust hover:underline">{u.email}</a>
                      </td>
                      <td className="px-5 py-4">
                        {u.phone
                          ? <a href={`tel:${u.phone}`} className="font-body text-sm text-mid-brown">{u.phone}</a>
                          : <span className="font-body text-sm text-light-brown/50">—</span>
                        }
                      </td>
                      <td className="px-5 py-4">
                        <span className={`font-body text-xs font-semibold px-2.5 py-1 rounded-full ${roleColors[u.role] || roleColors.user}`}>
                          {u.role === 'admin' ? 'Admin' : 'Customer'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`font-body text-xs px-2.5 py-1 rounded-full ${u.email_verified ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                          {u.email_verified ? '✓ Verified' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-body text-xs text-light-brown">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-border-custom">
              {filtered.map((u, idx) => (
                <div key={u.id || idx} className="p-4 hover:bg-cream/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-full bg-rust/10 flex items-center justify-center shrink-0">
                      {u.picture
                        ? <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                        : <User className="w-4 h-4 text-rust" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-body font-semibold text-dark-brown text-sm truncate">{u.name || '—'}</div>
                      <div className="font-body text-xs text-light-brown truncate">{u.email}</div>
                    </div>
                    <span className={`font-body text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${roleColors[u.role] || roleColors.user}`}>
                      {u.role === 'admin' ? 'Admin' : 'Customer'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 pl-12">
                    {u.phone && (
                      <a href={`tel:${u.phone}`} className="flex items-center gap-1 font-body text-xs text-mid-brown">
                        <Phone className="w-3 h-3" /> {u.phone}
                      </a>
                    )}
                    <span className={`font-body text-xs px-2 py-0.5 rounded-full ${u.email_verified ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                      {u.email_verified ? '✓ Verified' : 'Pending'}
                    </span>
                    {u.created_at && (
                      <span className="flex items-center gap-1 font-body text-xs text-light-brown">
                        <Clock className="w-3 h-3" />
                        {new Date(u.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {filtered.length > 0 && filtered.length < users.length && (
          <p className="font-body text-xs text-light-brown text-center mt-4">
            Showing {filtered.length} of {users.length} users
          </p>
        )}
      </div>
    </AdminLayout>
  );
}
